/**
 * Landing-only animated version of the brand symbol. Uses stroked paths for
 * the cross so each element draws in via stroke-dashoffset, then fades the
 * fills back on for the final, painted look. The plain `<Logo />` component
 * stays unchanged for the rest of the app.
 */

type Props = {
  size?: number;
  className?: string;
  withHalo?: boolean;
  animateDraw?: boolean;
  ariaLabel?: string;
};

export function LogoMark({
  size = 220,
  className,
  withHalo = true,
  animateDraw = true,
  ariaLabel = 'Orthodox Way',
}: Props) {
  const drawClass = animateDraw ? 'ow-logo-mark' : undefined;

  return (
    <div
      className={`relative inline-block ${className ?? ''}`}
      style={{ width: size, height: size, color: 'var(--ow-gold)' }}
    >
      {withHalo && (
        <span
          aria-hidden="true"
          className="ow-logo-halo absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(closest-side, var(--ow-gold-glow), transparent 70%)',
          }}
        />
      )}
      <div
        className={
          animateDraw ? 'ow-logo-breathe relative w-full h-full' : 'relative w-full h-full'
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 200 200"
          width={size}
          height={size}
          role="img"
          aria-label={ariaLabel}
          className={`relative z-10 ${drawClass ?? ''}`}
        >
          <g>
            {/* circle */}
            <path
              className={animateDraw ? 'ow-stroke' : undefined}
              d="M 123.22 193.15 A 96 96 0 1 0 76.78 193.15"
              fill="none"
              stroke="currentColor"
              strokeWidth={6}
              style={
                animateDraw
                  ? ({
                      ['--len' as never]: 605,
                      ['--draw-delay' as never]: '0ms',
                    } as React.CSSProperties)
                  : undefined
              }
            />
            {/* post (vertical) */}
            <rect
              x={93}
              y={33}
              width={14}
              height={166}
              fill="currentColor"
              className={animateDraw ? 'ow-fill' : undefined}
              style={
                animateDraw
                  ? ({ ['--draw-delay' as never]: '900ms' } as React.CSSProperties)
                  : undefined
              }
            />
            {/* titulus */}
            <rect
              x={75}
              y={51}
              width={50}
              height={13}
              fill="currentColor"
              className={animateDraw ? 'ow-fill' : undefined}
              style={
                animateDraw
                  ? ({ ['--draw-delay' as never]: '1500ms' } as React.CSSProperties)
                  : undefined
              }
            />
            {/* crossbar */}
            <rect
              x={45}
              y={82}
              width={110}
              height={13}
              fill="currentColor"
              className={animateDraw ? 'ow-fill' : undefined}
              style={
                animateDraw
                  ? ({ ['--draw-delay' as never]: '2100ms' } as React.CSSProperties)
                  : undefined
              }
            />
            {/* suppedaneum */}
            <path
              d="M 78 132 L 122 157 L 122 170 L 78 145 Z"
              fill="currentColor"
              className={animateDraw ? 'ow-fill' : undefined}
              style={
                animateDraw
                  ? ({ ['--draw-delay' as never]: '2700ms' } as React.CSSProperties)
                  : undefined
              }
            />
          </g>
        </svg>
        {animateDraw && <span aria-hidden className="ow-logo-shine" />}
      </div>
    </div>
  );
}
